import { memo, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconFolderOutline18 as FolderClosed,
	IconPlusOutline18 as Plus,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import type { TagsGet } from "@productlane/api"
import { cn } from "@productlane/lib/src/browser"
import {
	Dialog,
	Form,
	FormInput,
	ProjectIconKeys,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { IconPicker } from "@/components/color-picker"
import { PopoverButton } from "@/components/popover-button"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { mutateOk, useAuthData, useZero } from "@/lib/zero"
import { CreateTagGroupDialog } from "../tagGroups/create-tag-group-dialog"

export type PartialTagCreate = Pick<
	TagsGet[number],
	"id" | "name" | "color" | "icon" | "tagGroupId"
>

interface CreateTagDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean, created?: PartialTagCreate) => void
	title?: string
	defaultTagGroupId?: string
}

const CreateTagFormSchema = z.object({
	title: z.string().min(1),
	tagGroupId: z.string(),
	icon: z.nativeEnum(ProjectIconKeys),
})

export const CreateTagDialog = memo(
	({
		isOpen,
		onOpenChange,
		defaultTagGroupId,
		title,
	}: CreateTagDialogProps) => {
		const decodedToken = useAuthData()
		const zero = useZero()
		const [tagGroups] = useQuery(queries.tagGroups())

		const navigate = useNavigate()
		const [isCreateTagGroupOpen, setIsCreateTagGroupOpen] = useState(false)

		const defaultValues = useMemo(
			() => ({
				tagGroupId: defaultTagGroupId || tagGroups[0]?.id || "",
				icon: ProjectIconKeys.Label,
				title: title ?? "",
			}),
			[defaultTagGroupId, tagGroups, title],
		)

		const form = useZodForm({
			schema: CreateTagFormSchema,
			defaultValues,
		})

		const iconWatch = form.watch("icon")
		const tagGroupId = form.watch("tagGroupId")

		const selectedTagGroup = useMemo(
			() => tagGroups.find(({ id }) => id === tagGroupId),
			[tagGroupId, tagGroups],
		)

		useEffect(() => {
			form.reset({
				...form.getValues(),
				tagGroupId: defaultTagGroupId || tagGroups[0]?.id || "",
			})

			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isOpen])

		const options = useMemo(() => {
			const tagGroupOptions = tagGroups.map(({ name, id }) => ({
				label: (
					<div className="flex items-center">
						<FolderClosed
							className="text-gray-600 dark:text-gray-400"
							strokeWidth={1.75}
						/>
						<span className="ml-2">{name}</span>
					</div>
				),
				value: id,
			}))

			// Add "Create tag group" option if no tag groups exist

			tagGroupOptions.push({
				label: (
					<div
						className={cn(
							"flex items-center ",
							tagGroups.length > 0 ? "" : "text-blue-600 dark:text-blue-400",
						)}
					>
						<Plus strokeWidth={1.75} />
						<span className="ml-2">Create tag group</span>
					</div>
				),
				value: "create-tag-group",
			})

			return tagGroupOptions
		}, [tagGroups])

		useEffect(() => {
			form.setValue("title", title ?? "")
		}, [title, form])

		const ref = useRef<HTMLDivElement>(null)
		ref.current?.children?.[0]?.setAttribute("style", "height: 100%")

		const handleTagGroupChange = (value: string | null) => {
			if (value === "create-tag-group") {
				setIsCreateTagGroupOpen(true)
				return
			}
			form.setValue("tagGroupId", value ?? "")
		}

		const handleCreateTagGroupClose = (
			open: boolean,
			createdTagGroup?: { id: string; name: string; color: string },
		) => {
			setIsCreateTagGroupOpen(open)
			// If tag group was created, select the newly created tag group
			if (!open && createdTagGroup) {
				form.setValue("tagGroupId", createdTagGroup.id)
			}
		}

		useShortcut(
			"meta+enter, ctrl+enter",
			(e) => {
				e.preventDefault()
				if (isOpen) {
					// If no tag groups exist or no tag group is selected, open tag group creator
					if (tagGroups.length === 0 || !form.getValues("tagGroupId")) {
						setIsCreateTagGroupOpen(true)
						return
					}

					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					form.handleSubmit(async (values) => {
						const groupColor =
							tagGroups.find((g) => g.id === values.tagGroupId)?.color ?? ""
						const id = generateId()
						const result = await zero.mutate(
							mutators.tags.create({
								id,
								color: groupColor,
								icon: values.icon,
								name: values.title,
								tagGroupId: values.tagGroupId,
								productboardId: null,
								summaryString: "",
								summaryUpdated: Date.now(),
							}),
						).client
						if (result.type === "success") {
							onOpenChange(false, {
								id,
								color: groupColor,
								name: values.title,
								tagGroupId: values.tagGroupId,
								icon: values.icon,
							})
							toast.success({
								title: "Tag created",
								descriptionAction: {
									label: `${values.title}`,
									callback: () =>
										void navigate({ to: "/tags/$id", params: { id } }),
								},
							})

							form.reset(defaultValues)
						} else {
							console.error("Error handling form submission:", result.error)
							captureError(result.error, {
								domain: "general",
								action: "create tag",
								workspaceId: decodedToken.workspaceId,
								userId: decodedToken.userId,
							})
						}
					})()
				}
			},
			{
				// The dialog mounts inside a Radix overlay, which the shared guard treats
				// as a reason to swallow every key.
				skipDefaultGuards: true,
				enableOnFormTags: true,
			},
		)

		return (
			<>
				<Dialog
					isOpen={isOpen}
					onOpenChange={(open) => onOpenChange(open, undefined)}
					className="m-2 p-0"
					position="right"
					title={
						<>
							<div className="p-6">Create tag</div>
							<hr />
						</>
					}
					withOverlay={false}
					onCloseAutoFocus={(event) => event.preventDefault()}
					onInteractOutside={(event) => event.preventDefault()}
					modal={false}
				>
					<Form
						id="create-tag-form"
						form={form}
						className="flex flex-col"
						onSubmit={async (values) => {
							// If no tag groups exist or no tag group is selected, open tag group creator
							if (tagGroups.length === 0 || !values.tagGroupId) {
								setIsCreateTagGroupOpen(true)
								return
							}

							const groupColor =
								tagGroups.find((g) => g.id === values.tagGroupId)?.color ?? ""
							const id = generateId()
							await mutateOk(
								zero.mutate(
									mutators.tags.create({
										id,
										color: groupColor,
										icon: values.icon,
										name: values.title,
										tagGroupId: values.tagGroupId,
										productboardId: null,
										summaryString: null,
										summaryUpdated: Date.now(),
									}),
								),
							)
							onOpenChange(false, {
								id,
								color: groupColor,
								name: values.title,
								tagGroupId: values.tagGroupId,
								icon: values.icon,
							})
							toast.success({
								title: "Tag created",
								descriptionAction: {
									label: `${values.title}`,
									callback: () =>
										void navigate({ to: "/tags/$id", params: { id } }),
								},
							})
							form.reset(defaultValues)
						}}
					>
						<div className="flex items-start gap-4 px-6 pb-6 pt-2">
							<IconPicker
								icon={iconWatch}
								onIconChange={(icon) => {
									form.setValue("icon", icon)
								}}
								color={selectedTagGroup?.color}
							/>
							<FormInput
								textSize="xl"
								variant="borderless"
								name="title"
								placeholder="Tag name"
							/>
						</div>

						<hr />

						<div className="p-6">
							<div className="flex items-center justify-between">
								{/* Group */}
								<PopoverButton
									variant="outline"
									onChange={handleTagGroupChange}
									data={options}
									selected={tagGroupId}
									placeholder="Select tag group..."
									name="Select tag group"
									icon={FolderClosed}
									noResultsLabel="No tag groups exist. Create one first."
								/>

								<SubmitButton form="create-tag-form" disabled={false}>
									Create tag
								</SubmitButton>
							</div>
						</div>
					</Form>
				</Dialog>

				<CreateTagGroupDialog
					isOpen={isCreateTagGroupOpen}
					onOpenChange={handleCreateTagGroupClose}
				/>
			</>
		)
	},
)
